import React, { useEffect, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { X, Lock, ShieldCheck, Check, RefreshCcw, CreditCard } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import logo from "../assets/trimo-logo.png";

const ACCENT = { pro: "#38BDF8", premium: "#FACC15", vip: "#C084FC" };

const Summary = ({ plan, yearly, L, pick, money }) => {
  const c = ACCENT[plan?.id] || "#FACC15";
  const monthly = plan ? (yearly ? plan.price * 0.6 : plan.price) : 0;
  return (
    <div className="relative flex flex-col h-full px-6 sm:px-10 py-8 lg:py-14 overflow-hidden" data-testid="checkout-summary">
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full blur-3xl opacity-25" style={{ background: c }} />
      <div className="relative flex items-center gap-2.5"><img src={logo} alt="Vitled" className="h-9 w-9 rounded-lg" /><span className="text-lg font-extrabold text-white tracking-tight">Vitled</span></div>
      <p className="relative mt-10 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{L("Estás suscribiéndote a", "You're subscribing to")}</p>
      <h2 className="relative mt-2 text-4xl sm:text-5xl font-black tracking-tight" style={{ color: c }} data-testid="checkout-plan-name">{plan ? plan.id.toUpperCase() : ""}</h2>
      {plan && <p className="relative mt-3 flex items-end gap-2"><span className="text-3xl font-extrabold text-white" data-testid="checkout-plan-price">{money(monthly)}</span><span className="text-zinc-400 mb-1">{L("/mes", "/mo")}</span>
        {yearly && <span className="mb-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 px-2 py-0.5 text-[11px] font-black text-emerald-300">-40%</span>}</p>}
      {plan && <p className="relative text-sm text-zinc-400">{yearly ? L(`Facturado anualmente · ${money(monthly * 12)}/año`, `Billed yearly · ${money(monthly * 12)}/year`) : L("Facturado mensualmente", "Billed monthly")}</p>}
      {plan && <ul className="relative mt-8 space-y-3">{plan.features.filter((f) => f.ok).map((f, i) => (
        <li key={i} className="flex items-start gap-3 text-sm text-zinc-200"><span className="mt-0.5 h-5 w-5 rounded-full grid place-items-center shrink-0" style={{ background: `${c}26` }}><Check className="h-3 w-3" style={{ color: c }} /></span>{pick(f)}</li>
      ))}</ul>}
      <div className="relative mt-auto pt-10 grid grid-cols-3 gap-3 text-[11px] text-zinc-400">
        <span className="flex flex-col items-center gap-1.5 text-center"><ShieldCheck className="h-5 w-5 text-zinc-300" />{L("Pago cifrado SSL", "SSL encrypted")}</span>
        <span className="flex flex-col items-center gap-1.5 text-center"><RefreshCcw className="h-5 w-5 text-zinc-300" />{L("Cancela cuando quieras", "Cancel anytime")}</span>
        <span className="flex flex-col items-center gap-1.5 text-center"><CreditCard className="h-5 w-5 text-zinc-300" />{L("Procesado por Stripe", "Powered by Stripe")}</span>
      </div>
    </div>
  );
};

// Full-screen on-site Stripe checkout: same page & URL, no redirect to stripe.com.
export const EmbeddedCheckoutModal = ({ data, onClose }) => {
  const { L, pick, lang } = useLang();
  const stripePromise = useMemo(() => loadStripe(data.publishable_key), [data.publishable_key]);
  const money = (v) => (lang === "es" ? `${v.toFixed(2).replace(".", ",")}$` : `$${v.toFixed(2)}`);
  useEffect(() => {
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", esc); };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] bg-[#0B0B0C] overflow-y-auto animate-in fade-in duration-300" data-testid="embedded-checkout-modal">
      <button onClick={onClose} data-testid="embedded-checkout-close" aria-label="Cerrar"
        className="fixed top-4 right-4 z-20 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><X className="h-5 w-5" /></button>
      <div className="min-h-full grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="border-b lg:border-b-0 lg:border-r border-white/10 bg-zinc-950"><Summary plan={data.plan} yearly={data.yearly} L={L} pick={pick} money={money} /></div>
        <div className="bg-white flex flex-col">
          <div className="flex items-center gap-2 px-6 sm:px-10 pt-8 text-sm font-bold text-zinc-900"><Lock className="h-4 w-4 text-emerald-600" />{L("Pago seguro", "Secure payment")}</div>
          <div className="flex-1 px-2 sm:px-6 py-4 lg:py-8" data-testid="embedded-checkout-frame">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: data.client_secret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
